import { useState } from 'react';
import styled from 'styled-components';
import { FlexCenter, FlexColumn } from '@elementary/common-ui/components';
import { CircularProgress } from '@mui/material';
import AddTestDialog from 'shared/components/AddTestWizard/AddTestDialog';
import { EDITABLE_ASSET_TYPES } from 'shared/constants';
import useSnackbar from 'shared/hooks/useSnackbar';
import { IntegrationType } from 'shared/types';
import { parseConditionalSeverity } from 'shared/utils/utils';
import PanelError from 'shared/components/PanelError';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import useTestConfiguration from '../../hooks/useTestConfiguration';
import { TestOverview } from '../../types';
import AnomalyTestConfiguration from './AnomalyTestConfiguration';
import CodeTestConfiguration from './CodeTestConfiguration';
import DataContractTestConfiguration from './DataContractTestConfiguration';
import TestNotificationAccordion from './TestNotificationAccordion';

const resourceTypeMap: Record<string, 'tables' | 'columns'> = {
  TABLE: 'tables',
  COLUMN: 'columns',
};

const Wrapper = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.common.gray15};
  border-radius: 8px;
  padding: 16px 24px;
  gap: 24px;
`;

type Props = {
  testOverview: TestOverview;
};

const TestConfiguration = ({ testOverview }: Props) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const {
    testConfigData,
    testConditionalAlertDestinationsData,
    isTestConditionalAlertDestinationsLoading,
    isTestConfigLoading,
    isTestConfigFetching,
    muteTest,
    unMuteTest,
    isMuteTestLoading,
    testConfigError,
  } = useTestConfiguration({ testId: testOverview.execution?.testId });

  useViewLoadTracking({
    name: 'test configuration panel',
    loadDeps: [testConfigData],
    contextId: testOverview.execution?.testId || undefined,
  });

  const testConfig = testConfigData?.test?.config;

  const { openSnackbar } = useSnackbar();

  const handleMuteTestChanged = (val: boolean) =>
    val ? unMuteTest() : muteTest();

  const allowEdit =
    testConfig?.type === 'yaml' &&
    EDITABLE_ASSET_TYPES.includes(
      testConfigData?.test.tableAsset?.tableType.toLowerCase() || '',
    );

  const resourceType =
    resourceTypeMap[
      testConfigData?.test?.resource?.type || resourceTypeMap.TABLE
    ];

  if (isTestConfigLoading) {
    return (
      <Wrapper>
        <FlexCenter sx={{ height: '100%', padding: '0 24px' }}>
          <CircularProgress size={32} />
        </FlexCenter>
      </Wrapper>
    );
  }

  if (testConfigError) {
    return (
      <PanelError
        error={testConfigError as Error}
        errorName="ApiError"
        page={'TestConfiguration'}
      />
    );
  }

  return (
    <Wrapper>
      {(testConfig?.type === 'yaml' ||
        testConfig?.type === 'query' ||
        testConfig?.type === 'source_freshness') && (
        <CodeTestConfiguration
          testConfig={testConfig}
          onEdit={allowEdit ? () => setIsEditDialogOpen(true) : undefined}
        />
      )}

      {(testConfig?.type === 'anomaly_detection' ||
        testConfig?.type === 'freshness_anomaly_detection' ||
        testConfig?.type === 'pipeline_task_performance') && (
        <AnomalyTestConfiguration testOverview={testOverview} />
      )}

      {testConfig?.type === 'data_contract' && (
        <DataContractTestConfiguration testOverview={testOverview} />
      )}

      <TestNotificationAccordion
        isMuted={!!testConfig?.muted}
        onChange={handleMuteTestChanged}
        isLoading={
          isMuteTestLoading ||
          isTestConfigFetching ||
          isTestConditionalAlertDestinationsLoading
        }
        latestExecutionTestId={testOverview.execution?.testId}
        alertDestinations={testConditionalAlertDestinationsData?.test?.conditionalAlertDestinations.map(
          (d) => ({
            ...d,
            type: d.type.toLowerCase() as IntegrationType,
          }),
        )}
      />

      {testConfig?.type === 'yaml' && (
        <AddTestDialog
          open={isEditDialogOpen}
          mode={resourceType}
          onClose={() => setIsEditDialogOpen(false)}
          onSubmitSuccess={() =>
            openSnackbar({
              message:
                'Creating PR for your suggested changes... This may take a few seconds',
            })
          }
          editMode
          editTestMetadata={{
            name: testConfigData?.test?.name,
            type: testConfigData?.test?.type,
            subType: testOverview.execution?.subType,
            resourceName: testConfigData?.test?.resource?.name,
            tableName: testConfigData?.test?.tableAsset.name,
            package: testConfigData?.test?.namespace,
          }}
          initialValues={{
            resourceType,
            resources: [testConfigData?.test?.tableAsset.id || ''],
            yaml: testConfig.config,
            description: testConfig.description,
            owners: testConfig.owners,
            tags: testConfig.tags,
            severity: parseConditionalSeverity(
              testConfig.severity,
              testConfig.errorIf,
              testConfig.warnIf,
            ),
          }}
        />
      )}
    </Wrapper>
  );
};

export default TestConfiguration;
